import React, { useState, useEffect } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { HelpCircle, Upload, FileText, Search, X, Tag, SortAsc, LogOut, Settings } from 'lucide-react';
import { Logo } from '@/components/ui/logo';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useOfferStore, useQuoteTotals, useFilteredPositions } from '@/lib/store';
import { useAuth } from '@/lib/auth';
import { Badge } from '@/components/ui/badge';
import SettingsModal from './settings-modal';
import { debounce } from '@/lib/utils';
import { Position } from '@shared/schema';
import { 
  Dialog as QuoteDialog,
  DialogContent as QuoteDialogContent,
  DialogHeader as QuoteDialogHeader,
  DialogTitle as QuoteDialogTitle, 
  DialogTrigger as QuoteDialogTrigger
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { CustomerCombobox, SoftecClient } from './customer-combobox';

interface HeaderProps {
  onReturnToUpload?: () => void;
  showNavigation?: boolean;
}

export default function Header({ onReturnToUpload, showNavigation = false }: HeaderProps) {
  const [location, navigate] = useLocation();
  const { logout, sessionVia, userName, hasInvoiceAccess } = useAuth();
  const { toast } = useToast();
  const quoteItems = useOfferStore((state) => state.quoteItems);
  const isDataLoaded = useOfferStore((state) => state.isDataLoaded);
  const isGAEBDatabase = useOfferStore((state) => state.isGAEBDatabase);
  const quoteHeader = useOfferStore((state) => state.quoteHeader);
  const updateQuoteHeader = useOfferStore((state) => state.updateQuoteHeader);
  const totals = useQuoteTotals();
  const totalNet = Object.values(totals.taxGroups).reduce((sum, group) => sum + group.net, 0);
  const totalTax = Object.values(totals.taxGroups).reduce((sum, group) => sum + group.tax, 0);
  const totalGross = totalNet + totalTax;
  const { total: totalPositions } = useFilteredPositions();

  // App settings (company name from settings)
  const [companyName, setCompanyName] = useState<string>('Fortuna Softec');

  // Load app settings on mount
  useEffect(() => {
    fetch('/api/pdf-settings')
      .then(r => r.json())
      .then(data => {
        if (data.success && data.data?.companyName) {
          setCompanyName(data.data.companyName);
        }
      })
      .catch(() => {});
  }, []);

  // Logout confirmation state
  const [showLogoutDialog, setShowLogoutDialog] = useState(false);

  // Quote dialog state
  const [showQuoteDialog, setShowQuoteDialog] = useState(false);
  const [hasOpenedQuoteDialog, setHasOpenedQuoteDialog] = useState(false);

  // Check if quote header is incomplete (for glowing effect)
  const isQuoteIncomplete = !quoteHeader.kunde || !quoteHeader.angebot_nr || !quoteHeader.datum ||
                            !quoteHeader.anschrift || !quoteHeader.bauvorhaben || !quoteHeader.lz;

  // Only pulse when data is incomplete AND user hasn't opened the dialog yet
  const shouldPulse = isQuoteIncomplete && !hasOpenedQuoteDialog;
  
  // Search state and functions
  const searchTerm = useOfferStore((state) => state.searchTerm);
  const nameSearchTerm = useOfferStore((state) => state.nameSearchTerm);
  const setSearchTerm = useOfferStore((state) => state.setSearchTerm);
  const setNameSearchTerm = useOfferStore((state) => state.setNameSearchTerm);
  const resetSearch = useOfferStore((state) => state.resetSearch);
  const setSort = useOfferStore((state) => state.setSort);
  

  
  // Local state for input fields
  const [generalInputValue, setGeneralInputValue] = useState(searchTerm);
  const [nameInputValue, setNameInputValue] = useState(nameSearchTerm);
  
  // Update inputs when store values change
  useEffect(() => {
    setGeneralInputValue(searchTerm);
  }, [searchTerm]);
  
  useEffect(() => {
    setNameInputValue(nameSearchTerm);
  }, [nameSearchTerm]);
  
  // Debounced search functions
  const debouncedGeneralSearch = React.useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 300),
    [setSearchTerm]
  );
  
  const debouncedNameSearch = React.useCallback(
    debounce((value: string) => {
      setNameSearchTerm(value);
    }, 300),
    [setNameSearchTerm]
  );
  
  // Handle input changes
  const handleGeneralChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setGeneralInputValue(value);
    debouncedGeneralSearch(value);
  };
  
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setNameInputValue(value);
    debouncedNameSearch(value);
  };
  
  // Handle reset
  const handleReset = () => {
    resetSearch();
    setGeneralInputValue('');
    setNameInputValue('');
  };
  

  
  // Table column definitions matching the actual table structure

  
  return (
    <TooltipProvider>
      <header className="fixed top-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-b border-gray-200 shadow-sm z-50 transition-all duration-200">
        <div className="px-4 py-2">
          {/* Main header row */}
          <div className="flex justify-between items-center">
            {/* Left side - Logo and title */}
            <div className="flex items-center">
              <Logo height={36} className="mr-4 transition-transform hover:scale-105" />
              <div className="flex flex-col">
                <h1 className="text-xl font-bold text-gray-800">{companyName}</h1>
                <p className="text-sm text-gray-600">Angebot & Rechnung</p>
                {/* Angebotsnummer klein anzeigen wenn vorhanden */}
                {showNavigation && isDataLoaded && quoteHeader.angebot_nr && (
                  <p className="text-xs text-blue-600 font-medium">
                    Nr: {quoteHeader.angebot_nr}
                  </p>
                )}
              </div>
              <div className="flex ml-6 border-b border-gray-200">
                <button
                  type="button"
                  onClick={() => navigate('/')}
                  className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                  location === '/' || location.startsWith('/voice') || location.startsWith('/database') || location.startsWith('/excel') || location.startsWith('/gaeb') || location.startsWith('/pdf-lv')
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                >
                  Angebote
                </button>
                {hasInvoiceAccess ? (
                  <button
                    type="button"
                    onClick={() => navigate('/invoices')}
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    location === '/invoices'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  >
                    Rechnungen
                  </button>
                ) : (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button
                        disabled
                        aria-disabled="true"
                        className="px-3 py-1.5 text-sm font-medium border-b-2 border-transparent text-gray-300 cursor-not-allowed bg-transparent"
                      >
                        Rechnungen
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>Rechnungen nur per SSO-Zugang über Softec verfügbar</p>
                    </TooltipContent>
                  </Tooltip>
                )}
                {hasInvoiceAccess && (
                  <button
                    type="button"
                    onClick={() => navigate('/leistungsstand')}
                    className={`px-3 py-1.5 text-sm font-medium border-b-2 transition-colors ${
                    location === '/leistungsstand'
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                  >
                    Leistungsstand
                  </button>
                )}
              </div>
            </div>
            
            {/* Center - Angebotsdaten always visible, search only outside GAEB mode */}
            {showNavigation && isDataLoaded && (
              <div className="hidden lg:flex items-center gap-4 flex-1 max-w-5xl mx-8">
                {/* Angebotsdaten Button - Glowing Orange */}
                <QuoteDialog open={showQuoteDialog} onOpenChange={(open) => {
                    setShowQuoteDialog(open);
                    if (open) setHasOpenedQuoteDialog(true);
                  }}>
                  <QuoteDialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className={`transition-all duration-300 flex-shrink-0 ${
                        isQuoteIncomplete
                          ? shouldPulse
                            ? 'animate-slow-pulse bg-gradient-to-r from-orange-100 to-amber-100 border-orange-300 text-orange-700 shadow-lg shadow-orange-200/50 hover:from-orange-200 hover:to-amber-200'
                            : 'bg-gradient-to-r from-orange-100 to-amber-100 border-orange-300 text-orange-700 hover:from-orange-200 hover:to-amber-200'
                          : 'bg-gradient-to-r from-green-100 to-emerald-100 border-green-300 text-green-700 hover:from-green-200 hover:to-emerald-200'
                      }`}
                    >
                      {isQuoteIncomplete ? 'Angebotsdaten' : 'Angebotsdaten'}
                    </Button>
                  </QuoteDialogTrigger>
                  <QuoteDialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                    <QuoteDialogHeader>
                      <QuoteDialogTitle>Angebotsdaten bearbeiten</QuoteDialogTitle>
                    </QuoteDialogHeader>
                    <div className="space-y-4">
                      {/* Erste Reihe - Angebots-Nr. und Datum */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="angebot_nr">Angebots-Nr.</Label>
                          <Input
                            id="angebot_nr"
                            value={quoteHeader.angebot_nr}
                            onChange={(e) => updateQuoteHeader('angebot_nr', e.target.value)}
                            placeholder="A-2024-001"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="datum">Berlin, Datum</Label>
                          <Input
                            id="datum"
                            type="date"
                            value={quoteHeader.datum}
                            onChange={(e) => updateQuoteHeader('datum', e.target.value)}
                          />
                        </div>
                      </div>
                      
                      {/* Kunde */}
                      <div>
                        <Label htmlFor="kunde">Kunde</Label>
                        <CustomerCombobox
                          value={quoteHeader.kunde}
                          onChange={(value) => updateQuoteHeader('kunde', value)}
                          placeholder="Kunde auswählen..."
                          onSelect={(client: SoftecClient | null) => {
                            if (client) {
                              updateQuoteHeader('kunde', client.name);
                              // Auto-fill address if available
                              if (client.address) {
                                const parts: string[] = [];
                                if (client.address.street) {
                                  parts.push(`${client.address.street}${client.address.houseNumber ? ` ${client.address.houseNumber}` : ''}`);
                                }
                                if (client.address.zip || client.address.city) {
                                  parts.push(`${client.address.zip || ''} ${client.address.city || ''}`.trim());
                                }
                                if (parts.length > 0) {
                                  updateQuoteHeader('anschrift', parts.join('\n'));
                                }
                              }
                            }
                          }}
                        />
                        <p className="text-xs text-gray-500 mt-1">Klicken um Kunden aus Softec zu suchen</p>
                      </div>
                      
                      {/* Anschrift */}
                      <div>
                        <Label htmlFor="anschrift">Anschrift</Label>
                        <Textarea
                          id="anschrift"
                          value={quoteHeader.anschrift || ''}
                          onChange={(e) => updateQuoteHeader('anschrift', e.target.value)}
                          placeholder="Straße, Hausnummer&#10;PLZ Ort"
                          rows={2}
                        />
                      </div>

                      {/* Adresszusatz */}
                      <div>
                        <Label htmlFor="adresszusatz">Adresszusatz</Label>
                        <Input
                          id="adresszusatz"
                          value={quoteHeader.adresszusatz || ''}
                          onChange={(e) => updateQuoteHeader('adresszusatz', e.target.value)}
                          placeholder="z.B. c/o, z.Hd., Abteilung..."
                        />
                      </div>

                      {/* Bauvorhaben */}
                      <div>
                        <Label htmlFor="bauvorhaben">Bauvorhaben</Label>
                        <Textarea
                          id="bauvorhaben"
                          value={quoteHeader.bauvorhaben}
                          onChange={(e) => updateQuoteHeader('bauvorhaben', e.target.value)}
                          placeholder="Beschreibung des Bauvorhabens..."
                          rows={3}
                        />
                      </div>
                      
                      {/* Gewerk und Auftrag */}
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="gewerk">Gewerk</Label>
                          <Input
                            id="gewerk"
                            value={quoteHeader.gewerk || ''}
                            onChange={(e) => updateQuoteHeader('gewerk', e.target.value)}
                            placeholder="z.B. Elektroinstallation"
                          />
                        </div>
                        
                        <div>
                          <Label htmlFor="auftrag">Auftrag</Label>
                          <Input
                            id="auftrag"
                            value={quoteHeader.auftrag || ''}
                            onChange={(e) => updateQuoteHeader('auftrag', e.target.value)}
                            placeholder="Auftragsnummer"
                          />
                        </div>
                      </div>
                      
                      {/* Eingereichte Unterlagen */}
                      <div>
                        <Label htmlFor="eingereichte_unterlagen">Eingereichte Unterlagen</Label>
                        <Textarea
                          id="eingereichte_unterlagen"
                          value={quoteHeader.eingereichte_unterlagen || ''}
                          onChange={(e) => updateQuoteHeader('eingereichte_unterlagen', e.target.value)}
                          placeholder="Liste der eingereichten Unterlagen..."
                          rows={2}
                        />
                      </div>
                      
                      {/* LZ (Leistungszeitraum) */}
                      <div>
                        <Label htmlFor="lz">LZ (Leistungszeitraum)</Label>
                        <Input
                          id="lz"
                          value={quoteHeader.lz || ''}
                          onChange={(e) => updateQuoteHeader('lz', e.target.value)}
                          placeholder="z.B. 01.05.2026 - 31.05.2026"
                        />
                      </div>
                      
                      <Button
                        onClick={() => {
                          setShowQuoteDialog(false);
                        }}
                        className="w-full"
                      >
                        Speichern
                      </Button>
                    </div>
                  </QuoteDialogContent>
                </QuoteDialog>

                {!isGAEBDatabase && (
                  <>
                    {/* General Search - Made smaller */}
                    <div className="relative flex-1 min-w-0">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-4 w-4 text-gray-400" />
                      </div>
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            type="text"
                            placeholder="Allgemeine Suche..."
                            className="pl-10 pr-10 h-9 text-sm border border-gray-200 focus:border-blue-400"
                            value={generalInputValue}
                            onChange={handleGeneralChange}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Sucht in allen Feldern: Pos.-Nr., Gewerk, Name, Beschreibung, Einheit und Preis</p>
                        </TooltipContent>
                      </Tooltip>
                      
                      {generalInputValue && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setGeneralInputValue('');
                              debouncedGeneralSearch('');
                            }}
                          >
                            <X className="h-3 w-3 text-gray-400" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Name-Only Search - Made smaller */}
                    <div className="relative w-48">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Tag className="h-4 w-4 text-blue-400" />
                      </div>
                      
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Input
                            type="text"
                            placeholder="Name-Suche..."
                            className="pl-10 pr-10 border border-blue-200 focus:border-blue-400 h-9 text-sm"
                            value={nameInputValue}
                            onChange={handleNameChange}
                          />
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>Sucht nur in der Name-Spalte für präzise Ergebnisse</p>
                        </TooltipContent>
                      </Tooltip>
                      
                      {nameInputValue && (
                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0"
                            onClick={() => {
                              setNameInputValue('');
                              debouncedNameSearch('');
                            }}
                          >
                            <X className="h-3 w-3 text-blue-400" />
                          </Button>
                        </div>
                      )}
                    </div>

                    {/* Reset Button - Made smaller */}
                    {(generalInputValue || nameInputValue) && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleReset}
                        className="px-3 h-9 flex-shrink-0 hover:bg-red-50 hover:border-red-300 transition-colors"
                      >
                        <X className="h-3 w-3 mr-1" />
                        Reset
                      </Button>
                    )}
                  </>
                )}
              </div>
            )}
            
            {/* Center - Navigation summary (when data is loaded but search is hidden on smaller screens) */}
            {showNavigation && isDataLoaded && (
              <div className="lg:hidden flex items-center space-x-1 bg-gray-50 rounded-lg p-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="flex items-center gap-2 hover:bg-white transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Datei</span>
                </Button>
                
                <div className="h-6 w-px bg-gray-300 mx-2" />
                
                <div className="flex items-center gap-2 text-sm px-2">
                  <div className="flex items-center gap-1">
                    <FileText className="h-4 w-4 text-gray-500" />
                    <span className="font-medium">{quoteItems.length}</span>
                  </div>
                  
                  {totalNet > 0 && (
                    <div className="flex items-center gap-1">
                      <span className="font-semibold text-green-600 text-xs">
                        {new Intl.NumberFormat('de-DE', { 
                          style: 'currency', 
                          currency: 'EUR',
                          notation: 'compact'
                        }).format(totalGross)}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Right side - Actions */}
            <div className="flex items-center space-x-2">
              {/* New File Button - moved to right side */}
              {showNavigation && isDataLoaded && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="hidden lg:flex items-center gap-2 hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <Upload className="h-4 w-4" />
                  <span>Zurück</span>
                </Button>
              )}
              
              {/* Position Counter and Sort Reset - moved to right side */}
              {showNavigation && isDataLoaded && (
                <div className="hidden lg:flex items-center gap-3 pl-4 border-l border-gray-300">
                  <div className="text-xs text-gray-600">
                    <span className="font-medium">{totalPositions}</span> Positionen
                  </div>
                  
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleReset}
                    className="h-7 px-2 text-xs hover:bg-gray-100"
                    title="Alle Filter und Sortierung zurücksetzen"
                  >
                    <SortAsc className="h-3 w-3 mr-1" />
                    Reset
                  </Button>
                </div>
              )}
              
              {/* PDF Settings - always visible */}
              <SettingsModal
                trigger={
                  <Button variant="outline" size="sm" className="gap-1.5 transition-colors">
                    <Settings className="h-4 w-4" />
                    <span className="hidden sm:inline">Einstellungen</span>
                  </Button>
                }
              />

              {/* Help Dialog */}
              <Dialog>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm" className="gap-1.5 transition-colors">
                    <HelpCircle className="h-4 w-4" />
                    <span className="hidden sm:inline">Hilfe</span>
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle className="text-center">
                      <div className="mb-2">
                        <h2 className="text-2xl font-bold text-primary">{companyName} Angebot & Rechnung</h2>
                        <p className="text-sm text-muted-foreground mt-1">Professionelle Angebots- und Rechnungserstellung v2.0</p>
                      </div>
                      <div className="text-lg font-medium text-gray-800">Anleitung</div>
                    </DialogTitle>
                    <DialogDescription>
                      So erstellen Sie Angebote und Rechnungen mit {companyName}
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 pt-4 text-sm">
                    <div>
                      <h3 className="font-medium text-primary">📊 1. Startseite - Datenbanken & Angebote verwalten</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Farbcodierung (Source-Type Badges):</strong>
                        <br/>🔵 <strong>Manuell:</strong> Blau - Händisch erstellte Preislisten
                        <br/>🟢 <strong>Excel:</strong> Grün - Aus Excel importierte Preislisten
                        <br/>🟣 <strong>KI/Sprache:</strong> Lila - Per Spracheingabe erstellte Preislisten
                        <br/>🟠 <strong>X83:</strong> Orange - Importierte Ausschreibungen (X83 → X84 Workflow)
                        <br/><br/><strong>Aktionen (3-Punkte-Menü):</strong>
                        <br/>• <strong>Umbenennen:</strong> Namen direkt ändern
                        <br/>• <strong>Download:</strong> Als JSON oder Excel exportieren
                        <br/>• <strong>Löschen:</strong> Dauerhaft entfernen
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🔄 1.1 Angebot vs. Datenbank</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Datenbank:</strong> Wiederverwendbare Preisliste ohne Mengen (Katalog)
                        <br/><strong>Angebot:</strong> Spezifische Positionen mit Mengen für ein konkretes Projekt
                        <br/><strong>Beim Öffnen eines Angebots:</strong> Wählen Sie zwischen leerer Datenbank-Kopie oder Angebot mit Mengen
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">📁 2. Daten importieren</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Excel-Import:</strong> .xlsx, .xls, .csv, .ods Dateien direkt laden
                        <br/><strong>X83-Import:</strong> Ausschreibungen (.x83, .xml) importieren
                        <br/><strong>Automatische Erkennung:</strong> Spalten für Artikel-ID, Gewerk, Name, Beschreibung, Einheit, Preis
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🔍 3. Suchfunktion</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Allgemeine Suche:</strong> Durchsucht alle Felder gleichzeitig
                        <br/><strong>Name-Suche:</strong> Durchsucht nur die Name-Spalte für präzise Ergebnisse
                        <br/><strong>Echtzeit-Filterung:</strong> Ergebnisse werden sofort während der Eingabe aktualisiert
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🛒 4. Positionen auswählen</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Mengen eingeben:</strong> Geben Sie Werte direkt im Mengenfeld ein
                        <br/><strong>Dezimalzahlen:</strong> Bis zu 2 Nachkommastellen unterstützt
                        <br/><strong>Sofortige Berechnung:</strong> Preise und MwSt werden automatisch berechnet
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">🏗️ 5. Gewerk-Organisation</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Automatische Gruppierung:</strong> Positionen werden nach Gewerken sortiert
                        <br/><strong>Drag & Drop:</strong> Reihenfolge der Gewerke in der Sidebar anpassbar
                        <br/><strong>Nummerierung:</strong> Gewerke erhalten automatisch fortlaufende Nummern
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">👤 6. Angebotsdaten & Kundensuche</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>Kunde auswählen:</strong> Klicken Sie auf das Kunde-Feld um aus Softec zu suchen
                        <br/><strong>Auto-Vervollständigung:</strong> Adresse wird automatisch übernommen
                        <br/><strong>Manuelle Eingabe:</strong> Auch direkte Eingabe ohne Softec-Auswahl möglich
                        <br/><strong>Angebotsnummer:</strong> Automatische Generierung oder manuelle Eingabe
                      </p>
                    </div>

                    <div>
                      <h3 className="font-medium text-primary">📄 7. Angebot exportieren</h3>
                      <p className="text-muted-foreground mt-1">
                        <strong>PDF Export:</strong> Professionelles PDF-Angebot mit eigener Vorlage
                        <br/><strong>Softec Export:</strong> Direkter Export zur Softec-App (Projekt + Budget)
                        <br/><strong>Excel/JSON Download:</strong> Über 3-Punkte-Menü auf der Startseite
                        <br/><strong>MwSt-Berechnung:</strong> Separate Ausweisung nach Steuersätzen
                      </p>
                    </div>

                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                      <h3 className="font-medium text-blue-800">💡 Tipps & Neue Features</h3>
                      <p className="text-blue-700 text-xs mt-1">
                        <strong>Kundensuche:</strong> Softec-Kunden direkt im Angebots-Formular suchen
                        <br/><strong>3-Punkte-Menü:</strong> Umbenennen, Download (JSON/Excel), Löschen
                        <br/><strong>Source-Type Badges:</strong> Erkenne sofort die Herkunft jeder Datenbank
                        <br/><strong>KI-Beschreibungen:</strong> Lila "Beschreibung erweitern" Button für detaillierte Texte
                        <br/><strong>X83-Editor:</strong> Preise eingeben und als X84-Angebot exportieren
                        <br/><strong>ESC-Taste:</strong> Schnell zur Startseite zurückkehren
                      </p>
                    </div>
                  </div>
                  <DialogClose asChild>
                    <Button className="mt-4">Verstanden</Button>
                  </DialogClose>
                </DialogContent>
              </Dialog>


              {/* User Info + Logout */}
              <div className="flex items-center gap-2 ml-2">
                {sessionVia === 'sso' && userName && (
                  <div className="hidden sm:flex items-center gap-1.5">
                    <span className="text-xs text-gray-600">{userName}</span>
                    <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-5 bg-blue-50 text-blue-700 border-blue-200">
                      SSO
                    </Badge>
                  </div>
                )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowLogoutDialog(true)}
                  className="gap-1.5 transition-colors"
                >
                  <LogOut className="h-4 w-4" />
                  <span className="hidden sm:inline">Abmelden</span>
                </Button>
              </div>
            </div>
          </div>
          

          
          {/* Mobile search row - appears below table controls on mobile when data is loaded */}
          {showNavigation && isDataLoaded && (
            <div className="lg:hidden mt-3 pt-3 border-t border-gray-200">
              <div className="flex gap-2">
                {/* New File Button */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onReturnToUpload}
                  className="flex items-center gap-1 hover:bg-blue-50 hover:border-blue-300 transition-colors flex-shrink-0"
                >
                  <Upload className="h-4 w-4" />
                  <span className="hidden sm:inline">Neue Datei</span>
                  <span className="sm:hidden">Neu</span>
                </Button>
                
                {/* General Search */}
                <div className="relative flex-1">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-4 w-4 text-gray-400" />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Allgemeine Suche..."
                    className="pl-10 pr-10 h-9"
                    value={generalInputValue}
                    onChange={handleGeneralChange}
                  />
                  
                  {generalInputValue && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setGeneralInputValue('');
                          debouncedGeneralSearch('');
                        }}
                      >
                        <X className="h-4 w-4 text-gray-400" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Name-Only Search */}
                <div className="relative w-32 sm:w-40">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Tag className="h-4 w-4 text-blue-400" />
                  </div>
                  
                  <Input
                    type="text"
                    placeholder="Name..."
                    className="pl-10 pr-10 border-blue-200 focus:border-blue-400 h-9"
                    value={nameInputValue}
                    onChange={handleNameChange}
                  />
                  
                  {nameInputValue && (
                    <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={() => {
                          setNameInputValue('');
                          debouncedNameSearch('');
                        }}
                      >
                        <X className="h-4 w-4 text-blue-400" />
                      </Button>
                    </div>
                  )}
                </div>

                {/* Reset Button */}
                {(generalInputValue || nameInputValue) && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReset}
                    className="px-2 h-9 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Logout Confirmation Dialog */}
      <AlertDialog open={showLogoutDialog} onOpenChange={setShowLogoutDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Abmelden</AlertDialogTitle>
            <AlertDialogDescription>
              Möchten Sie sich wirklich abmelden?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Abbrechen</AlertDialogCancel>
            <AlertDialogAction onClick={() => logout()}>Abmelden</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TooltipProvider>
  );
}
